import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useIntent } from "@/context/IntentContext";

export function PublicNav() {
  const { user } = useAuth();
  const { open } = useIntent();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 1 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Close the mobile menu on navigation — render-adjust instead of an effect
  const [prevPath, setPrevPath] = useState(loc.pathname);
  if (prevPath !== loc.pathname) {
    setPrevPath(loc.pathname);
    setMobileOpen(false);
  }

  const navLink = (to: string, label: string, mobile = false) => {
    const isActive = loc.pathname === to;
    return (
      <Link
        to={to}
        className={`text-[12px] tracking-[0.08em] font-medium transition-colors ${
          mobile ? "block py-3 text-[14px]" : ""
        }`}
        style={{
          color: isActive ? "#f0ebe5" : "#9a918a",
          fontFamily: "'Figtree', sans-serif",
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.color = "#f0ebe5";
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.color = "#9a918a";
        }}
      >
        {label}
      </Link>
    );
  };

  return (
    <>
      <div ref={sentinelRef} className="absolute top-0 left-0 w-full h-[1px]" />
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled || mobileOpen ? "rgba(18,16,19,0.92)" : "transparent",
          backdropFilter: scrolled || mobileOpen ? "blur(16px)" : "none",
          borderBottom: scrolled
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid transparent",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div
              className="w-7 h-7 flex items-center justify-center flex-shrink-0"
              style={{ background: "#c8913a" }}
            >
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
                />
              </svg>
            </div>
            <span
              className="text-[14px] font-bold tracking-tight hidden sm:block"
              style={{
                color: "#f0ebe5",
                fontFamily: "'Archivo', sans-serif",
              }}
            >
              SellMore<span style={{ color: "#635c57" }}>Tickets</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLink("/features", "Features")}
            {navLink("/how-it-works", "How It Works")}
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            {user ? (
              <Link
                to="/dashboard"
                className="px-4 py-1.5 text-[12px] tracking-[0.06em] font-semibold transition-all hover:brightness-110"
                style={{
                  background: "#c8913a",
                  color: "#141215",
                  fontFamily: "'Figtree', sans-serif",
                }}
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-[12px] tracking-[0.06em] font-medium transition-colors hidden sm:block"
                  style={{
                    color: "#9a918a",
                    fontFamily: "'Figtree', sans-serif",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#f0ebe5")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#9a918a")}
                >
                  Log In
                </Link>
                <button
                  onClick={open}
                  className="px-4 sm:px-5 py-1.5 sm:py-2 text-[12px] tracking-[0.06em] font-semibold transition-all hover:brightness-110"
                  style={{
                    background: "#c8913a",
                    color: "#141215",
                    fontFamily: "'Figtree', sans-serif",
                  }}
                >
                  Get Started Free
                </button>
              </>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#9a918a" strokeWidth={2}>
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="md:hidden px-6 pb-6 pt-2"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            {navLink("/features", "Features", true)}
            {navLink("/how-it-works", "How It Works", true)}
            {!user && (
              <Link
                to="/login"
                className="block py-3 text-[14px] font-medium transition-colors"
                style={{ color: "#9a918a", fontFamily: "'Figtree', sans-serif" }}
              >
                Log In
              </Link>
            )}
          </div>
        )}
      </nav>
    </>
  );
}
